import React, { useCallback, useEffect, useState, useRef } from "react";
import { ScanFace } from "lucide-react";
import "../../../styles/account-settings.css";
import { PlanUsageCard } from "./PlanUsageCard";
import { api } from "../../../utils/api";
import { API_ENDPOINTS, S3_BASE_URL as S3_BASE_URL_WITH_SLASH } from "../../../config";
import { UserSubscription } from "../../../types/subscription";

const S3_BASE_URL = S3_BASE_URL_WITH_SLASH.replace(/\/$/, '');

export type PlanMock = {
  name: string;
  price: string;
  description: string;
  usage: {
    resumes: string;
    packets: string;
    tailors: string;
    reviews: string;
    generations: string;
    jobs: string;
    contacts: string;
    documents: string;
  };
};

type AccountSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  plan: PlanMock;
  profile?: { 
    firstName?: string; 
    lastName?: string; 
    email?: string;
    isEmailConfirmed?: boolean;
    weeklyDigest?: boolean;
    dailyDigest?: boolean;
    imgUrl?: string;
  };
  onProfileUpdate?: (firstName: string, lastName: string) => void;
  onPhotoUpdate?: (imgUrl: string) => void;
  userSubscription?: UserSubscription | null;
};

export const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  open,
  onClose,
  plan,
  profile,
  onProfileUpdate,
  onPhotoUpdate,
  userSubscription,
}) => {
  const [tab, setTab] = useState<"account" | "plan">("account");
  const [first, setFirst] = useState(profile?.firstName ?? "");
  const [last, setLast] = useState(profile?.lastName ?? "");
  const email = profile?.email ?? "";
  const [weekly, setWeekly] = useState(profile?.weeklyDigest ?? false);
  const [daily, setDaily] = useState(profile?.dailyDigest ?? false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const isEmailVerified = profile?.isEmailConfirmed ?? false;
  
  const [initialValues, setInitialValues] = useState({
    first: profile?.firstName ?? "",
    last: profile?.lastName ?? "",
    weekly: profile?.weeklyDigest ?? false,
    daily: profile?.dailyDigest ?? false,
  });
  
  useEffect(() => {
    if (profile) {
      setFirst(profile.firstName ?? "");
      setLast(profile.lastName ?? "");
      setWeekly(profile.weeklyDigest ?? false);
      setDaily(profile.dailyDigest ?? false);
      setInitialValues({
        first: profile.firstName ?? "",
        last: profile.lastName ?? "",
        weekly: profile.weeklyDigest ?? false,
        daily: profile.dailyDigest ?? false,
      });
    }
  }, [profile]);
  
  const hasChanges = 
    first !== initialValues.first ||
    last !== initialValues.last ||
    weekly !== initialValues.weekly ||
    daily !== initialValues.daily ||
    newPassword !== "" ||
    confirmPassword !== "";

  const [isSaving, setIsSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(profile?.imgUrl || "");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile?.imgUrl) {
      setPhotoUrl(profile.imgUrl);
    }
  }, [profile?.imgUrl]);

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    setIsUploadingPhoto(true);

    try {
      
      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.postFormData<{ img_url: string }>(API_ENDPOINTS.uploadMePhoto, formData);
      
      
      const newPhotoUrl = response.img_url;
      setPhotoUrl(newPhotoUrl);
      
      if (onPhotoUpdate) {
        onPhotoUpdate(newPhotoUrl);
      }
    } catch (error) {
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getPhotoUrl = () => {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http')) return photoUrl;
    return `${S3_BASE_URL}/${photoUrl}`;
  };

  const handleDeletePhoto = async () => {
    if (!photoUrl) return;
    
    setIsUploadingPhoto(true);
    
    try {
      
      await api.delete(API_ENDPOINTS.deleteMePhoto);
      
      
      setPhotoUrl("");
      
      if (onPhotoUpdate) {
        onPhotoUpdate("");
      }
    } catch (error) {
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleConfirmEmail = async () => {
    setIsSendingConfirmation(true);
    
    try {
      await api.post(API_ENDPOINTS.generateEmailConfirmation, {});
      setConfirmationSent(true);
    } catch (error) {
    } finally {
      setIsSendingConfirmation(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    
    try {
      
      await api.post(API_ENDPOINTS.updateMe, {
        first_name: first,
        last_name: last,
        weekly_digest: weekly,
        daily_digest: daily,
      });
      
      
      if (newPassword && confirmPassword) {
        await api.post(API_ENDPOINTS.updatePassword, {
          new_password: newPassword,
          confirm_password: confirmPassword,
        });
      }
      
      setInitialValues({
        first,
        last,
        weekly,
        daily,
      });
      
      if (onProfileUpdate) {
        onProfileUpdate(first, last);
      }
      
      setNewPassword("");
      setConfirmPassword("");
      
      onClose();
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const onEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onEsc]);

  if (!open) return null;

  return (
    <div className="as-Overlay">
      <div className="as-Modal">
        <div className="as-Body">
          <aside className="as-Rail">
            <div className="as-Identity">
              {photoUrl ? (
                <img src={getPhotoUrl()} alt="Avatar" className="as-Avatar as-AvatarImg" />
              ) : (
                <div className="as-Avatar as-Avatar--empty">
                  <ScanFace size={36} />
                </div>
              )}
              <div>
                <div className="as-IdentityName">{first} {last}</div>
                <div className="as-IdentityEmail">{email}</div>
              </div>
            </div>

            <nav className="as-Nav">
              <button
                className={`as-NavItem ${tab === "account" ? "is-active" : ""}`}
                onClick={() => setTab("account")}
              >
                My Account
              </button>
              <button
                className={`as-NavItem ${tab === "plan" ? "is-active" : ""}`}
                onClick={() => setTab("plan")}
              >
                Plan & Usage
              </button>
            </nav>

            <div className="as-RailFoot">
              <a 
                className="as-LinkDanger" 
                href={`mailto:support@offerflow.pro?subject=Account deletion - ${encodeURIComponent(email)}`}
              >
                Delete my account
              </a>
            </div>
          </aside>

          <section className="as-Content">
            {tab === "account" && (
              <div className="as-Section">
                <div className="as-SectionTitle">General Info</div>
                <div className="as-Divider" />
                <div className="as-Grid">
                  <div className="as-FieldCol">
                    <label className="as-Label">Profile Photo</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePhotoChange}
                    />
                    <div className="as-AvatarWrapper">
                      <div 
                        className={`as-Avatar as-AvatarLg ${!photoUrl ? 'as-AvatarClickable' : ''} ${isUploadingPhoto ? 'as-AvatarUploading' : ''}`}
                        onClick={!photoUrl ? handlePhotoClick : undefined}
                        title={!photoUrl ? "Click to upload photo" : undefined}
                      >
                        {photoUrl ? (
                          <img src={getPhotoUrl()} alt="Profile" className="as-AvatarImg" />
                        ) : (
                          <span className="as-AvatarPlaceholder">+</span>
                        )}
                        {isUploadingPhoto && <div className="as-AvatarLoader" />}
                      </div>
                      {photoUrl && !isUploadingPhoto && (
                        <button 
                          type="button"
                          className="as-AvatarDelete"
                          onClick={handleDeletePhoto}
                          title="Delete photo"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="as-InputsCol">
                    <div className="as-NameRow">
                      <div className="as-NameField">
                        <label className="as-Label">First Name</label>
                        <input 
                          className="as-Input" 
                          value={first} 
                          onChange={(e)=>setFirst(e.target.value)}
                          autoComplete="given-name"
                        />
                      </div>
                      <div className="as-NameField">
                        <label className="as-Label">Last Name</label>
                        <input 
                          className="as-Input" 
                          value={last} 
                          onChange={(e)=>setLast(e.target.value)}
                          autoComplete="family-name"
                        />
                      </div>
                    </div>
                    
                    <div className="as-EmailDisplay">
                      <div className="as-EmailText">{email}</div>
                      {isEmailVerified ? (
                        <div className="as-EmailVerified">
                          <span className="as-EmailVerifiedIcon">✓</span>
                          <span className="as-EmailVerifiedText">Email verified</span>
                        </div>
                      ) : confirmationSent ? (
                        <div className="as-EmailVerified">
                          <span className="as-EmailVerifiedIcon">✉</span>
                          <span className="as-EmailVerifiedText">Confirmation sent</span>
                        </div>
                      ) : (
                        <button 
                          className="as-EmailConfirmBtn" 
                          onClick={handleConfirmEmail}
                          disabled={isSendingConfirmation}
                        >
                          {isSendingConfirmation ? 'Sending...' : 'Confirm email'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="as-SectionTitle mt16">Change Password</div>
                <div className="as-Divider" />
                <div className="as-PasswordSection">
                  <div className="as-PasswordRow">
                    <div className="as-PasswordField">
                      <label className="as-Label">New Password</label>
                      <input 
                        type="password" 
                        className="as-Input" 
                        value={newPassword} 
                        onChange={(e)=>setNewPassword(e.target.value)} 
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="as-PasswordField">
                      <label className="as-Label">Confirm New Password</label>
                      <input 
                        type="password" 
                        className="as-Input" 
                        value={confirmPassword} 
                        onChange={(e)=>setConfirmPassword(e.target.value)} 
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </div>

                <div className="as-SectionTitle mt16">Email Subscriptions</div>
                <div className="as-Divider" />
                <div className="as-Row">
                  <div className="as-RowText">Weekly Digest</div>
                  <input type="checkbox" checked={weekly} onChange={(e)=>setWeekly(e.target.checked)} />
                </div>
                <div className="as-Row">
                  <div className="as-RowText">Daily Digest</div>
                  <input type="checkbox" checked={daily} onChange={(e)=>setDaily(e.target.checked)} />
                </div>

                <div className="as-SectionTitle mt16">Coupons</div>
                <div className="as-Divider" />
                <div className="as-Empty">No Coupons</div>
              </div>
            )}

            {tab === "plan" && (
              <PlanUsageCard plan={plan} userSubscription={userSubscription} />
            )}
          </section>
        </div>

        <div className="as-Footer">
          <button className="as-Btn" onClick={onClose}>Close</button>
          <button 
            className="as-Btn as-BtnPrimary" 
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            style={{ 
              opacity: hasChanges && !isSaving ? 1 : 0.5, 
              cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed' 
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

    </div>
  );
};

type ConfirmDialogProps = {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, description, confirmLabel, onConfirm, onCancel
}) => {
  const onKey = useCallback((e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); }, [onCancel]);

  useEffect(() => {
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onKey]);

  return (
    <div className="as-ConfirmWrap">
      <div className="as-ConfirmBackdrop" onClick={onCancel} />
      <div className="as-Confirm">
        <div className="as-ConfirmHeader">{title}</div>
        <div className="as-ConfirmBody">{description}</div>
        <div className="as-ConfirmFooter">
          <button className="as-Btn as-BtnPrimary" onClick={onConfirm}>{confirmLabel}</button>
          <button className="as-Btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};